import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import Icon from 'app/components/common/icon';

class StandardInput extends React.PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      filled: false,
      fileName: '',
    };

    this.refInput = React.createRef();

    this.onFocus = this.onFocus.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onClickFake = this.onClickFake.bind(this);
    this.onClickClear = this.onClickClear.bind(this);
  }

  componentDidMount() {
    if (this.props.focusOnMount) {
      this.element?.focus();
    }
  }

  onFocus(event) {
    this.setState({filled: true});
    this.props.onFocus && this.props.onFocus(event);
  }

  onBlur(event) {
    const hasValue = (this.props.type === 'file')
      ? this.refInput.current.files[0]
      : this.refInput.current.value.trim();
    if (!hasValue) this.setState({filled: false});
    this.props.onBlur && this.props.onBlur(event);
  }

  onChange(event) {
    const { onChange, type, maxChars } = this.props;
    if (type === 'file') {
      const file = _.get(this.refInput, 'current.files[0]');
      if (!file) return;
      this.setState({ fileName: file.name });
      onChange && onChange(event, file);
    } else {
      let val = event.target.value;
      const isOverLimit = ((maxChars || 0) > 0) && (val.length > maxChars);
      if (isOverLimit) val = val.substring(0, maxChars);
      onChange && onChange(event, val);
    }
  }

  onClickFake() {
    this.refInput.current.click();
  }

  onClickClear() {
    const {onChange, type} = this.props;
    this.setState({fileName: '', filled: false});
    this.refInput.current.value = null;
    onChange && onChange(null, null);
  }

  click() {
    this.refInput.current.click();
  }

  get value() {
    return this.refInput.current.value;
  }

  set value(newVal) {
    this.refInput.current.value = newVal;
  }

  get element() {
    return this.refInput.current;
  }

  render() {
    const { filled } = this.state;
    const { type, name, label, validations, validationMessage, icon, prefix, prefixWidth, rightIcon, className, disabled, showClear, noResize, elevateFocus, maxChars, focusOnMount, ...inputProps } = this.props;
    const isFile = type === 'file';
    const isTextarea = type === 'textarea';
    const vMsg = validationMessage || _.get(validations, `${name}[0]`);
    const filledClass = (filled || inputProps.value || inputProps.defaultValue) ? 'filled' : '';
    const invalidClass = vMsg ? 'invalid' : '';
    const iconClass = icon ? 'has-icon' : 'no-icon';
    const disabledClass = disabled ? 'disabled' : '';
    const efClass = elevateFocus ? 'elevate-focus' : '';
    const hasValue = isFile ? !!this.state.fileName : !!inputProps.value;

    const actualProps = {
      ref: this.refInput,
      type,
      name,
      disabled,
      placeholder: label,
      ...inputProps,
      onFocus: this.onFocus,
      onBlur: this.onBlur,
      onChange: this.onChange,
    };

    if (isFile) actualProps.value = undefined;

    const inputCss = {};
    if (noResize && isTextarea) inputCss.resize = 'none';
    // '15' is the horizontal position of the prefix, in 'px':
    if (prefix) inputCss.paddingLeft = prefixWidth + 15;

    const [maxCharsLabel, isMaxed] = (() => {
      if (isFile) return [null, false];
      if (!maxChars || (maxChars < 0)) return [null, false];
      const count = inputProps.value?.length || 0;
      return [`${count} / ${maxChars}`, count >= maxChars];
    })();

    return (
      <div className={`standard-input ${filledClass} ${className} ${invalidClass} ${iconClass} ${disabledClass} ${efClass}`}>
        {!!prefix && <div className="prefix">{prefix}</div>}
        {isTextarea 
          ? <textarea {...actualProps} style={inputCss} /> 
          : <input {...actualProps} style={inputCss} />}
        {isFile &&
          <input
            className={`${filledClass} fake-file`}
            type="text"
            placeholder={label}
            value={this.state.fileName || inputProps.value || ''}
            onClick={this.onClickFake}
            onFocus={this.onFocus}
            onBlur={this.onBlur}
            readOnly
          />
        }
        {icon}
        <div className="right-icon">
          {rightIcon}
        </div>
        {showClear && (
          <button className="standard-input-clear-btn" onClick={this.onClickClear}><Icon.Remove /></button>
        )}
        <div className="standard-input-meta _flex _flex-row _justify-start _align-start">
          {!!vMsg
            ? <div className="validation-message _flex-1">{vMsg}</div>
            : <div className="_flex-1" />
          }
          {maxCharsLabel && (
            <div className={`standard-input-max-chars ${isMaxed ? 'maxed' : ''}`}>{maxCharsLabel}</div>
          )}
        </div>
      </div>
    );
  }

}

StandardInput.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  type: PropTypes.string,
  className: PropTypes.string,
  icon: PropTypes.node,
  prefix: PropTypes.string,
  prefixWidth: PropTypes.number,
  rightIcon: PropTypes.node,
  validations: PropTypes.object,
  validationMessage: PropTypes.string,
  showClear: PropTypes.bool,
  noResize: PropTypes.bool,
  autoComplete: PropTypes.string,
  elevateFocus: PropTypes.bool,
  maxChars: PropTypes.number,
  focusOnMount: PropTypes.bool,
};

StandardInput.defaultProps = {
  type: 'text',
  validations: {},
  className: '',
  showClear: false,
  noResize: false,
  autoComplete: 'off',
  maxChars: null, // make sure to use second arg in onChange if this is set
  // set true to raise z index when focused - useful when border overlaps another element, but don't want to globally set this
  elevateFocus: false,
  /** Text to be displayed within the input box, to the left of the actual input
   *  area, where the icon would appear. Unlike the placeholder, this text will
   *  always be visible. This prop is not compatible with `icon`. */
  prefix: null,
  /** The pixel distance by which the actual input area should be shifted, if
   *  `prefix` is set, to make room for that text. */
  prefixWidth: 25,
  focusOnMount: false,
};

export default StandardInput;
